const DB_NAME = "claimsdesk-inspection";
// v2 added ACTIONS: the outbox for things the inspector DID that the server has
// not heard yet (finishing a job in a dead spot). Photos were durable from the
// start; the decision to finish was not.
const DB_VERSION = 2;
const UPLOADS = "uploads";
const JOBS = "jobs";
const JOB_LISTS = "job_lists";
const ACTIONS = "actions";

function dbKey(userId, value) {
  return `${userId}:${value}`;
}

// ONE connection for the tab, not one per operation. The queue reads run on
// every upload progress tick and every queue change; opening and closing a
// database each time turned routine uploading into a stream of connection
// churn on the phone.
let connection = null;

function openDb() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("Offline storage is not available in this browser."));
  }
  if (connection) return connection;
  connection = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      connection = null;
      reject(request.error);
    };
    // Another tab still holding the old version blocks the upgrade. Without this
    // the promise never settles and every store call after it hangs silently —
    // the worst possible failure for the thing holding the day's photos.
    request.onblocked = () => {
      connection = null;
      reject(new Error("Close the other ClaimsDesk tabs on this device and reload."));
    };
    request.onsuccess = () => {
      const db = request.result;
      // Reopen on the next call if the browser evicts or closes this handle.
      db.onclose = () => { connection = null; };
      db.onversionchange = () => {
        db.close();
        connection = null;
      };
      resolve(db);
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(UPLOADS)) {
        const uploads = db.createObjectStore(UPLOADS, { keyPath: "id" });
        uploads.createIndex("user_id", "userId");
        uploads.createIndex("inspection_id", "inspectionId");
      }
      if (!db.objectStoreNames.contains(JOBS)) {
        db.createObjectStore(JOBS, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(JOB_LISTS)) {
        db.createObjectStore(JOB_LISTS, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(ACTIONS)) {
        const actions = db.createObjectStore(ACTIONS, { keyPath: "id" });
        actions.createIndex("user_id", "userId");
      }
    };
  });
  return connection;
}

async function run(storeName, mode, action) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result;
    try {
      result = action(store);
    } catch (error) {
      reject(error);
      return;
    }
    tx.oncomplete = () => resolve(result?.result);
    tx.onerror = () => reject(tx.error || result?.error);
    tx.onabort = () => reject(tx.error || new Error("Offline storage transaction was cancelled."));
  });
}

export function notifyQueueChanged() {
  window.dispatchEvent(new CustomEvent("inspection-queue-changed"));
}

export async function putUpload(record) {
  await run(UPLOADS, "readwrite", (store) => store.put(record));
  notifyQueueChanged();
  return record;
}

export async function deleteUpload(id) {
  await run(UPLOADS, "readwrite", (store) => store.delete(id));
  notifyQueueChanged();
}

export async function listUploads(userId, inspectionId = null) {
  // Through the user_id index so a second account's photos on a shared phone are
  // never in the result. (It is not a cheaper read than getAll() — the records come
  // back whole either way, blobs included. countUploads is the cheap one, and that
  // is what the header badge uses.)
  const rows = await run(UPLOADS, "readonly", (store) =>
    store.index("user_id").getAll(Number(userId))
  );
  return (rows || [])
    .filter((row) => inspectionId == null || Number(row.inspectionId) === Number(inspectionId))
    // createdAt first (it survives a reload), then the in-session sequence that
    // separates photos picked within the same millisecond.
    .sort((a, b) =>
      String(a.createdAt).localeCompare(String(b.createdAt)) || (a.sequence || 0) - (b.sequence || 0)
    );
}

// Count without touching the blobs at all — this is the header badge's hot path.
export async function countUploads(userId) {
  return (await run(UPLOADS, "readonly", (store) =>
    store.index("user_id").count(Number(userId))
  )) || 0;
}

// --- The outbox (queued intentions, not bytes) --------------------------------
// Same shape as the upload queue on purpose: one record per pending action, keyed
// so re-doing the same thing REPLACES rather than stacks up.

export async function putAction(record) {
  await run(ACTIONS, "readwrite", (store) => store.put(record));
  notifyQueueChanged();
  return record;
}

export async function deleteAction(id) {
  await run(ACTIONS, "readwrite", (store) => store.delete(id));
  notifyQueueChanged();
}

export async function listActions(userId) {
  const rows = await run(ACTIONS, "readonly", (store) =>
    store.index("user_id").getAll(Number(userId))
  );
  return (rows || []).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
}

export async function cacheJob(userId, job) {
  await run(JOBS, "readwrite", (store) => store.put({
    key: dbKey(userId, job.inspection_id),
    userId,
    job,
    cachedAt: new Date().toISOString(),
  }));
}

export async function getCachedJob(userId, inspectionId) {
  const row = await run(JOBS, "readonly", (store) => store.get(dbKey(userId, inspectionId)));
  return row?.job || null;
}

// The job sheet rides in the JOBS store under its own key prefix rather than in a
// store of its own — it is the same kind of thing (one cached document per job) and
// this keeps the database at v2. It is cached at all because the sheet is what
// replaces the printout: a paper sheet works in a valley, so this has to as well.
const sheetKey = (userId, inspectionId) => dbKey(userId, `sheet:${inspectionId}`);

export async function cacheJobSheet(userId, inspectionId, sheet) {
  await run(JOBS, "readwrite", (store) => store.put({
    key: sheetKey(userId, inspectionId),
    userId,
    job: sheet,
    cachedAt: new Date().toISOString(),
  }));
}

export async function getCachedJobSheet(userId, inspectionId) {
  const row = await run(JOBS, "readonly", (store) => store.get(sheetKey(userId, inspectionId)));
  return row?.job || null;
}

export async function cacheJobList(userId, date, jobs) {
  await run(JOB_LISTS, "readwrite", (store) => store.put({
    key: dbKey(userId, date),
    userId,
    date,
    jobs,
    cachedAt: new Date().toISOString(),
  }));
  await Promise.all(jobs.map((job) => cacheJob(userId, job)));
}

export async function getCachedJobList(userId, date) {
  const row = await run(JOB_LISTS, "readonly", (store) => store.get(dbKey(userId, date)));
  return row?.jobs || null;
}
